/**
 * WorkEvidenceCenterPage — the Work & Evidence Center (read-only).
 *
 * One human-readable, time-ordered stream of the work the Digital Employees did and the
 * evidence that backs it: AI runs, their action audit rows, human approval tokens, verified
 * and unverified outcomes, tickets and conversations (including employee handoffs). Every
 * event is anchored to a recorded timestamp, links back to its source record, and carries a
 * clear verification state — מאומת / לא אומת / נכשל — where the record makes a claim.
 *
 * Sources that failed, are not permitted, or were not fetched are disclosed explicitly —
 * never shown as "nothing happened". Raw machine payloads (plan/analysis JSON, request and
 * response bodies, nonces, message technical traces) are not part of this surface; the
 * provenance links lead to the existing detail screens where the gates already live.
 *
 * Route: /work-evidence. Tickets data needs authentication only; run/action/approval/
 * conversation/employee data follows the server's agents_view gate.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { agentRunsApi, agentsApi, conversationsApi, ticketsApi } from "../services/api";
import { useAgentPermissions } from "../context/AgentPermissionsContext";
import EmployeeAvatar from "../components/EmployeeAvatar";
import { SOURCE_STATES, classifySource } from "../components/statusVocabulary";
import { useOnlineStatus, useRecoveredNotice } from "../components/useLiveState";
import {
  APPROVAL_RUN_CAP,
  CONVERSATIONS_LIMIT,
  DEFAULT_WINDOW_HOURS,
  EVENT_TYPES,
  EVENT_TYPE_META,
  MAX_RENDERED_EVENTS,
  RUNS_LIMIT,
  TICKETS_LIMIT,
  WINDOW_OPTIONS,
  WORK_STATES,
  WORK_STATE_META,
  buildWorkEvidenceEvents,
  countsByState,
  countsByType,
  filterWorkEvidenceEvents,
  formatEventClock,
  formatEventFull,
  groupEventsByDay,
} from "../components/WorkEvidenceCenter/workEvidenceModel";

const CHIP_TONE = {
  ok: "bg-white text-gray-700 border-gray-200",
  loading: "bg-white text-gray-400 border-gray-200",
  error: "bg-red-50 text-red-700 border-red-200",
  warn: "bg-amber-50 text-amber-700 border-amber-200",
  denied: "bg-gray-100 text-gray-500 border-gray-200",
};

const timeOf = (timestamp) => (timestamp ? new Date(timestamp).toLocaleTimeString("he-IL") : null);

// Chip wording comes from the shared classifySource ladder: a refresh failure after an earlier
// successful load is stale — the retained payload keeps rendering and the chip says so, with the
// time of the successful load, instead of claiming the source never loaded. A paused fetch is
// react-query's own offline telemetry.
function sourceChip(query, loadedValue, loadingValue = "טוען…") {
  const state = classifySource(query);
  if (state.key === SOURCE_STATES.failed) return { tone: "error", value: "לא נטען" };
  if (state.key === SOURCE_STATES.stale) {
    return { tone: "warn", value: `${loadedValue} (הרענון נכשל — הנתון מ־${timeOf(state.since)})` };
  }
  if (state.key === SOURCE_STATES.fresh) return { tone: "ok", value: loadedValue };
  if (query.fetchStatus === "paused") return { tone: "loading", value: "ממתין לחיבור…" };
  return { tone: "loading", value: loadingValue };
}

// Recovered-notice semantics live in components/useLiveState.js so every live surface shares one
// definition: only an observed degradation followed by a newer successful load announces recovery.

function SourceChip({ label, value, tone }) {
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs border rounded-full px-2.5 py-1 ${CHIP_TONE[tone] ?? CHIP_TONE.ok}`}>
      <span className="text-gray-400">{label}:</span>
      <span>{value}</span>
    </span>
  );
}

function StateBadge({ state, stateLabel }) {
  const meta = WORK_STATE_META[state];
  if (!meta) return null;
  return (
    <span
      aria-label={`סטטוס אימות: ${stateLabel ?? meta.label}`}
      className={`text-[11px] border rounded-full px-1.5 py-0.5 font-medium ${meta.chip}`}
    >
      {stateLabel ?? meta.label}
    </span>
  );
}

function EventRow({ event, employeesById }) {
  const meta = EVENT_TYPE_META[event.type] ?? { label: event.type, chip: "bg-gray-100 text-gray-600 border-gray-200" };
  const employee = event.employeeIds.length > 0 ? employeesById[event.employeeIds[0]] : null;
  return (
    <li className="mr-4">
      <div className="absolute -right-1.5 mt-1.5 w-3 h-3 rounded-full bg-white border-2 border-gray-300" />
      <div className="space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          {employee && <EmployeeAvatar name={employee.name} avatarKey={employee.avatar_key} avatarColor={employee.avatar_color} size="sm" />}
          <span className={`text-[11px] border rounded-full px-1.5 py-0.5 ${meta.chip}`}>{meta.label}</span>
          {event.state && <StateBadge state={event.state} stateLabel={event.stateLabel} />}
          <span className="text-sm font-medium text-gray-800">{event.title}</span>
          <span className="text-xs text-gray-400" title={formatEventFull(event.atMs)}>
            {formatEventClock(event.atMs)}
          </span>
        </div>
        {event.detail && <p className="text-sm text-gray-600 break-words">{event.detail}</p>}
        {(event.chips.length > 0 || event.ref) && (
          <div className="flex items-center gap-2 flex-wrap text-xs text-gray-500">
            {event.chips.map((chip, index) => (
              <span key={index} className="bg-gray-100 text-gray-600 rounded px-1.5 py-0.5">
                {chip.label}
              </span>
            ))}
            {event.ref && <span>{event.ref}</span>}
          </div>
        )}
        {event.timeNote && <p className="text-[11px] text-amber-600">{event.timeNote}</p>}
        {event.link && (
          <div className="flex items-center gap-3 flex-wrap">
            <Link to={event.link.to} className="inline-block text-xs text-blue-600 hover:underline">
              {event.link.label}
            </Link>
            {event.secondaryLink && (
              <Link to={event.secondaryLink.to} className="inline-block text-xs text-blue-600 hover:underline">
                {event.secondaryLink.label}
              </Link>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

// Failed vs stale wording per source: the failed alert keeps the existing "not loaded — events may
// be missing" disclosure; the stale notice says the retained payload is being shown with the time
// of the successful load, and new events may be missing specifically since that load.
const SOURCE_ALERT_TEXT = {
  runs: {
    failed: "מקור נתוני ריצות ה-AI לא נטען — ייתכן וחסרים אירועי ריצות, פעולות ותוצאות. אין להסיק מכך שלא התרחשו.",
    staleSubject: "רענון מקור נתוני ריצות ה-AI נכשל",
    staleMiss: "ייתכן שחסרים אירועי ריצות, פעולות ותוצאות מאז הטעינה. אין להסיק מכך שלא התרחשו.",
  },
  tickets: {
    failed: "מקור נתוני הקריאות לא נטען — ייתכן וחסרים אירועי קריאות. אין להסיק מכך שלא התרחשו.",
    staleSubject: "רענון מקור נתוני הקריאות נכשל",
    staleMiss: "ייתכן שחסרים אירועי קריאות מאז הטעינה. אין להסיק מכך שלא התרחשו.",
  },
  conversations: {
    failed: "מקור נתוני השיחות לא נטען — ייתכן וחסרים אירועי שיחות והעברות. אין להסיק מכך שלא התרחשו.",
    staleSubject: "רענון מקור נתוני השיחות נכשל",
    staleMiss: "ייתכן שחסרים אירועי שיחות והעברות מאז הטעינה. אין להסיק מכך שלא התרחשו.",
  },
};

function SourceStateNotice({ query, texts }) {
  const state = classifySource(query);
  if (state.key === SOURCE_STATES.failed) {
    return (
      <p role="alert" className="text-xs bg-red-50 border border-red-200 rounded-lg p-2 text-red-700">
        {texts.failed}
      </p>
    );
  }
  if (state.key === SOURCE_STATES.stale) {
    return (
      <p role="status" className="text-xs bg-amber-50 border border-amber-200 rounded-lg p-2 text-amber-700">
        {`${texts.staleSubject} — מוצג הנתון מהטעינה המוצלחת מ־${timeOf(state.since)}. ${texts.staleMiss}`}
      </p>
    );
  }
  return null;
}

function StateTile({ label, value, tone, hint }) {
  return (
    <div className={`border rounded-xl px-3 py-2 min-w-[8rem] ${tone}`} title={hint}>
      <div className="text-lg font-bold leading-tight">{value}</div>
      <div className="text-xs">{label}</div>
    </div>
  );
}

export default function WorkEvidenceCenterPage() {
  const { can } = useAgentPermissions();
  const canViewAgents = can("agents_view");
  const online = useOnlineStatus();

  const [activeTypes, setActiveTypes] = useState(() => new Set(EVENT_TYPES));
  const [activeStates, setActiveStates] = useState(() => new Set());
  const [windowHours, setWindowHours] = useState(DEFAULT_WINDOW_HOURS);
  const [search, setSearch] = useState("");
  const [employeeId, setEmployeeId] = useState("");

  const runsQuery = useQuery({
    queryKey: ["work-evidence-runs"],
    queryFn: () => agentRunsApi.listAll({ limit: RUNS_LIMIT }),
    enabled: canViewAgents,
    staleTime: 30_000,
  });
  const ticketsQuery = useQuery({
    queryKey: ["work-evidence-tickets"],
    queryFn: () => ticketsApi.list({ limit: TICKETS_LIMIT }),
    staleTime: 30_000,
  });
  const conversationsQuery = useQuery({
    queryKey: ["work-evidence-conversations"],
    queryFn: () => conversationsApi.list({ limit: CONVERSATIONS_LIMIT }),
    enabled: canViewAgents,
    staleTime: 30_000,
  });
  const employeesQuery = useQuery({
    queryKey: ["work-evidence-employees"],
    queryFn: () => agentsApi.list(),
    enabled: canViewAgents,
    staleTime: 60_000,
  });

  const runs = useMemo(() => (Array.isArray(runsQuery.data) ? runsQuery.data : []), [runsQuery.data]);
  const tickets = useMemo(() => (Array.isArray(ticketsQuery.data) ? ticketsQuery.data : []), [ticketsQuery.data]);
  const conversations = useMemo(
    () => (Array.isArray(conversationsQuery.data) ? conversationsQuery.data : []),
    [conversationsQuery.data]
  );
  const employees = useMemo(() => (Array.isArray(employeesQuery.data) ? employeesQuery.data : []), [employeesQuery.data]);
  const employeesById = useMemo(
    () => Object.fromEntries(employees.filter((e) => e?.id).map((e) => [String(e.id), e])),
    [employees]
  );

  const candidateRunIds = useMemo(
    () => runs.slice(0, APPROVAL_RUN_CAP).map((run) => run?.id).filter(Boolean),
    [runs]
  );

  const approvalsQuery = useQuery({
    queryKey: ["work-evidence-approvals", candidateRunIds],
    queryFn: async () => {
      const results = await Promise.allSettled(candidateRunIds.map((runId) => agentRunsApi.approvalTokens(runId)));
      const tokensByRun = {};
      const failedRunIds = [];
      const forbiddenRunIds = [];
      results.forEach((result, index) => {
        const runId = candidateRunIds[index];
        if (result.status === "fulfilled") {
          tokensByRun[runId] = Array.isArray(result.value) ? result.value : [];
        } else if (result.reason?.response?.status === 403) {
          forbiddenRunIds.push(runId);
        } else {
          failedRunIds.push(runId);
        }
      });
      return { tokensByRun, failedRunIds, forbiddenRunIds };
    },
    enabled: canViewAgents && candidateRunIds.length > 0,
    staleTime: 60_000,
  });

  const approvals = approvalsQuery.data ?? null;
  const approvalsFailed = approvals?.failedRunIds?.length ?? 0;
  const approvalsForbidden = approvals?.forbiddenRunIds?.length ?? 0;
  const approvalTokensCount = approvals
    ? Object.values(approvals.tokensByRun).reduce((sum, list) => sum + list.length, 0)
    : 0;

  // The sources this viewer actually reads — permission- and candidate-gated exactly like the
  // queries themselves, so a disabled query is never mistaken for a loading one.
  const enabledSources = [ticketsQuery];
  if (canViewAgents) {
    enabledSources.push(runsQuery, conversationsQuery, employeesQuery);
    if (candidateRunIds.length > 0) enabledSources.push(approvalsQuery);
  }
  const enabledSourceStates = enabledSources.map((query) => classifySource(query).key);
  const anySourceLoading = enabledSourceStates.includes(SOURCE_STATES.loading);
  const anySourceFailed = enabledSourceStates.some(
    (key) => key === SOURCE_STATES.failed || key === SOURCE_STATES.stale
  );
  const anySourceFresh = enabledSourceStates.includes(SOURCE_STATES.fresh);
  const anySourceLoaded = enabledSourceStates.some(
    (key) => key === SOURCE_STATES.fresh || key === SOURCE_STATES.stale
  );
  const latestLoadedAt = Math.max(0, ...enabledSources.map((query) => query.dataUpdatedAt || 0));
  const recoveredAt = useRecoveredNotice({
    degraded: anySourceFailed,
    fresh: anySourceFresh,
    online,
    loadedAt: latestLoadedAt,
  });

  const { events: allEvents, undated } = useMemo(
    () => buildWorkEvidenceEvents({ runs, tickets, approvals, conversations }),
    [runs, tickets, approvals, conversations]
  );

  const nowMs = Date.now();
  const baseFilters = { windowHours, query: search, employeeId: employeeId || null, nowMs };
  // The type-context set keeps every type so chip counts stay stable while types are toggled,
  // and keeps every state so the state chips can show what selecting them would reveal.
  const contextEvents = filterWorkEvidenceEvents(allEvents, baseFilters);
  const typeCounts = countsByType(contextEvents);
  const stateCounts = countsByState(contextEvents);

  const filtered = filterWorkEvidenceEvents(allEvents, { ...baseFilters, types: activeTypes, states: activeStates });
  const shown = filtered.slice(0, MAX_RENDERED_EVENTS);
  const groups = groupEventsByDay(shown, nowMs);

  const undatedTotal = undated.run + undated.ticket + undated.action + undated.approval + undated.conversation;
  const undatedDetail = `ריצות ${undated.run} · קריאות ${undated.ticket} · פעולות ${undated.action} · אישורים ${undated.approval} · שיחות ${undated.conversation}`;

  const toggleType = (type) => {
    setActiveTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  const toggleState = (state) => {
    setActiveStates((prev) => {
      const next = new Set(prev);
      if (next.has(state)) next.delete(state);
      else next.add(state);
      return next;
    });
  };

  const filtersDirty =
    search !== "" ||
    employeeId !== "" ||
    windowHours !== DEFAULT_WINDOW_HOURS ||
    activeTypes.size !== EVENT_TYPES.length ||
    activeStates.size > 0;

  const clearFilters = () => {
    setSearch("");
    setEmployeeId("");
    setWindowHours(DEFAULT_WINDOW_HOURS);
    setActiveTypes(new Set(EVENT_TYPES));
    setActiveStates(new Set());
  };

  const refreshAll = () => {
    if (canViewAgents) {
      runsQuery.refetch();
      conversationsQuery.refetch();
      employeesQuery.refetch();
    }
    ticketsQuery.refetch();
    if (canViewAgents && candidateRunIds.length > 0) approvalsQuery.refetch();
  };

  const runsChip = !canViewAgents
    ? { tone: "denied", value: "אין הרשאה (agents_view)" }
    : sourceChip(runsQuery, `נטענו ${runs.length} ריצות${runs.length >= RUNS_LIMIT ? " (ייתכן וקיימות ותיקות יותר)" : ""}`);

  const ticketsChip = sourceChip(
    ticketsQuery,
    `נטענו ${tickets.length} קריאות${tickets.length >= TICKETS_LIMIT ? " (ייתכן וקיימות נוספות)" : ""}`
  );

  const approvalsBaseChip =
    candidateRunIds.length > 0
      ? sourceChip(
          approvalsQuery,
          `נטענו ${approvalTokensCount} אישורים עבור ${candidateRunIds.length} ריצות${
            runs.length > APPROVAL_RUN_CAP ? " (האחרונות בלבד)" : ""
          }`,
          `טוען אישורים עבור ${candidateRunIds.length} ריצות…`
        )
      : null;
  if (approvalsBaseChip && approvalsBaseChip.tone === "ok" && approvalsFailed + approvalsForbidden > 0) {
    approvalsBaseChip.tone = "warn";
  }

  const approvalsChip = !canViewAgents
    ? { tone: "denied", value: "אין הרשאה (agents_view)" }
    : candidateRunIds.length === 0
    ? runsQuery.isPending
      ? { tone: "loading", value: runsQuery.fetchStatus === "paused" ? "ממתין לחיבור…" : "טוען…" }
      : { tone: "warn", value: "אין ריצות לבדיקה" }
    : approvalsBaseChip;

  const conversationsChip = !canViewAgents
    ? { tone: "denied", value: "אין הרשאה (agents_view)" }
    : sourceChip(
        conversationsQuery,
        `נטענו ${conversations.length} שיחות${
          conversations.length >= CONVERSATIONS_LIMIT ? " (ייתכן וקיימות נוספות)" : ""
        }`
      );

  const employeesChip = !canViewAgents
    ? { tone: "denied", value: "אין הרשאה (agents_view)" }
    : sourceChip(employeesQuery, `נטענו ${employees.length} עובדים`);

  const uncertaintyNotes = [];
  if (approvalsFailed > 0) {
    uncertaintyNotes.push(`אישורי הרצות עבור ${approvalsFailed} ריצות לא נטענו — ייתכן וחסרים אירועי אישור.`);
  }
  if (approvalsForbidden > 0) {
    uncertaintyNotes.push(`אישורים אינם זמינים עבור ${approvalsForbidden} ריצות (אין הרשאה או שהתכונה אינה פעילה).`);
  }
  if (canViewAgents && runs.length > APPROVAL_RUN_CAP) {
    uncertaintyNotes.push(`נבדקו ${APPROVAL_RUN_CAP} הריצות האחרונות בלבד — ייתכן וחסרים אישורים של ריצות ותיקות יותר.`);
  }
  if (canViewAgents && runs.length >= RUNS_LIMIT) {
    uncertaintyNotes.push(`נטענו ${RUNS_LIMIT} הריצות האחרונות בלבד — ייתכן וחסרים אירועי ריצות ופעולות ותיקים יותר.`);
  }
  if (conversations.length >= CONVERSATIONS_LIMIT) {
    uncertaintyNotes.push(`נטענו ${CONVERSATIONS_LIMIT} השיחות האחרונות בלבד — ייתכן וחסרים אירועי שיחות ותיקים יותר.`);
  }
  if (undatedTotal > 0) {
    uncertaintyNotes.push(`לא הוצגו על הציר ${undatedTotal} רשומות ללא מועד תיעוד.`);
  }

  const someSourceUnknown =
    !canViewAgents ||
    (canViewAgents && (runsQuery.isError || conversationsQuery.isError)) ||
    ticketsQuery.isError ||
    approvalsFailed > 0 ||
    approvalsForbidden > 0;

  return (
    <div className="space-y-4" dir="rtl">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-gray-800">מרכז עבודה וראיות</h1>
          <p className="text-sm text-gray-500 mt-1">
            זרימה מאוחדת לקריאה בלבד של עבודת העובדים הדיגיטליים והראיות שמאחוריה: ריצות AI ופעולות, אישורים
            אנושיים, תוצאות מאומתות ולא מאומתות, כשלים, קריאות ושיחות. כל אירוע נגזר מהרשומה הרשמית שלו ומקושר אליה.
          </p>
        </div>
        <button
          type="button"
          onClick={refreshAll}
          className="text-xs border border-gray-300 rounded-lg px-2.5 py-1 text-gray-600 hover:bg-gray-50"
        >
          רענון
        </button>
      </header>

      <div className="flex flex-wrap gap-2">
        <SourceChip label="ריצות AI" value={runsChip.value} tone={runsChip.tone} />
        <SourceChip label="קריאות" value={ticketsChip.value} tone={ticketsChip.tone} />
        <SourceChip label="אישורים" value={approvalsChip.value} tone={approvalsChip.tone} />
        <SourceChip label="שיחות" value={conversationsChip.value} tone={conversationsChip.tone} />
        <SourceChip label="עובדים" value={employeesChip.value} tone={employeesChip.tone} />
      </div>

      {!canViewAgents && (
        <p className="text-xs bg-gray-50 border border-gray-200 rounded-lg p-2 text-gray-600">
          לצפייה באירועי ריצות, פעולות, אישורים, שיחות וסינון לפי עובד/ת נדרשת הרשאת agents_view — מוצגים אירועי
          קריאות בלבד.
        </p>
      )}
      {canViewAgents && <SourceStateNotice query={runsQuery} texts={SOURCE_ALERT_TEXT.runs} />}
      <SourceStateNotice query={ticketsQuery} texts={SOURCE_ALERT_TEXT.tickets} />
      {canViewAgents && <SourceStateNotice query={conversationsQuery} texts={SOURCE_ALERT_TEXT.conversations} />}

      {!online && (
        <p role="status" className="text-xs bg-gray-100 border border-gray-300 rounded-lg p-2 text-gray-700">
          {anySourceLoaded
            ? "אין חיבור לרשת — הרענון מושהה עד לחזרת החיבור. הנתונים המוצגים הם מהטעינה המוצלחת האחרונה."
            : "אין חיבור לרשת — הרענון מושהה עד לחזרת החיבור. המקורות טרם נטענו."}
        </p>
      )}

      {recoveredAt != null && (
        <p role="status" className="text-xs bg-emerald-50 border border-emerald-200 rounded-lg p-2 text-emerald-800">
          החיבור שוחזר — הרענון הושלם בהצלחה ב־{timeOf(recoveredAt)}.
        </p>
      )}

      <div className="bg-white rounded-xl border shadow-sm p-4 space-y-3">
        <div className="flex items-center gap-2 flex-wrap" role="group" aria-label="סינון לפי סוג אירוע">
          {EVENT_TYPES.map((type) => {
            const active = activeTypes.has(type);
            const meta = EVENT_TYPE_META[type];
            return (
              <button
                key={type}
                type="button"
                aria-pressed={active}
                onClick={() => toggleType(type)}
                className={`text-xs rounded-full border px-2.5 py-1 transition-colors ${
                  active ? meta.chip : "bg-white text-gray-400 border-gray-200 hover:bg-gray-50"
                }`}
              >
                {meta.label} ({typeCounts[type]})
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2 flex-wrap" role="group" aria-label="סינון לפי סטטוס אימות">
          <span className="text-xs text-gray-500">סטטוס אימות:</span>
          {WORK_STATES.map((state) => {
            const active = activeStates.has(state);
            const meta = WORK_STATE_META[state];
            return (
              <button
                key={state}
                type="button"
                aria-pressed={active}
                onClick={() => toggleState(state)}
                className={`text-xs rounded-full border px-2.5 py-1 transition-colors ${
                  active ? meta.chip : "bg-white text-gray-400 border-gray-200 hover:bg-gray-50"
                }`}
              >
                {meta.label} ({stateCounts[state]})
              </button>
            );
          })}
          <span className="text-[11px] text-gray-400">
            אירועי מידע ללא סטטוס אימות ({stateCounts.none}) מוצגים רק כשלא נבחר סטטוס.
          </span>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-xs text-gray-500">
            טווח זמן
            <select
              aria-label="טווח זמן"
              value={windowHours}
              onChange={(event) => setWindowHours(Number(event.target.value))}
              className="border border-gray-300 rounded-lg px-2 py-1 text-xs text-gray-700"
            >
              {WINDOW_OPTIONS.map((option) => (
                <option key={option.hours} value={option.hours}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          {canViewAgents && !employeesQuery.isError && (
            <label className="flex items-center gap-2 text-xs text-gray-500">
              עובד/ת
              <select
                aria-label="סינון לפי עובד/ת"
                value={employeeId}
                onChange={(event) => setEmployeeId(event.target.value)}
                className="border border-gray-300 rounded-lg px-2 py-1 text-xs text-gray-700"
              >
                <option value="">כל העובדים הדיגיטליים</option>
                {employees.map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {employee.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="flex items-center gap-2 text-xs text-gray-500 flex-1 min-w-[14rem]">
            חיפוש
            <input
              aria-label="חיפוש חופשי"
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="לפי כותרת, טקסט, מספר קריאה, עובד/ת או חברה"
              className="border border-gray-300 rounded-lg px-2 py-1 text-xs text-gray-700 w-full"
            />
          </label>
          {filtersDirty && (
            <button
              type="button"
              onClick={clearFilters}
              className="text-xs border border-gray-300 rounded-lg px-2.5 py-1 text-gray-600 hover:bg-gray-50"
            >
              נקה סינון
            </button>
          )}
        </div>
        <div className="flex items-center gap-3 flex-wrap text-xs text-gray-500">
          <span>
            מציג {shown.length} מתוך {filtered.length} אירועים בסינון הנוכחי
            {filtered.length !== allEvents.length ? ` (סה"כ במרכז: ${allEvents.length})` : ""}
          </span>
          {filtered.length > MAX_RENDERED_EVENTS && (
            <span className="text-amber-600">מוצגים {MAX_RENDERED_EVENTS} האירועים האחרונים — צמצמו את הטווח או השתמשו בחיפוש.</span>
          )}
        </div>
      </div>

      {activeStates.size > 0 && (
        <p className="text-xs bg-gray-50 border border-gray-200 rounded-lg p-2 text-gray-600">
          סינון לפי סטטוס אימות מציג רק אירועי עבודה עם סטטוס — תוצאות מאומתות, השלמות ללא אימות, וכשלים או דחיות.
        </p>
      )}

      <div className="bg-white rounded-xl border shadow-sm p-4 space-y-2">
        <h2 className="text-sm font-semibold text-gray-700">מצב אימות בטווח ובסינון הנוכחי</h2>
        <div className="flex flex-wrap gap-2">
          <StateTile label="מאומת" value={stateCounts.verified} tone="bg-green-50 border-green-200 text-green-800" hint="ריצות שאומתו בסגירת קריאה מאושרת שבוצעה על קריאה סגורה, ופעולות שביצוען אושר ברשומה." />
          <StateTile label="לא אומת" value={stateCounts.unverified} tone="bg-amber-50 border-amber-200 text-amber-800" hint="ריצות שהסתיימו בהצלחה ללא ראיית אימות במסך זה — ההצלחה לא אומתה; ראיות אימות אחרות עשויות להופיע במסך ניסיון העובד/ת." />
          <StateTile label="נכשל/נדחה" value={stateCounts.failed} tone="bg-red-50 border-red-200 text-red-800" hint="ריצות ופעולות שנכשלו או נדחו על ידי המאשר." />
        </div>
        <p className="text-[11px] text-gray-400">
          הספירה נגזרת מהאירועים הטעונים בלבד ({stateCounts.none} אירועי מידע ללא סטטוס אינם נספרים) — אין להסיק
          ממנה על אירועים שלא נטענו.
        </p>
      </div>

      {uncertaintyNotes.length > 0 && (
        <div className="space-y-1">
          {uncertaintyNotes.map((note, index) => (
            <p key={index} className="text-xs bg-amber-50 border border-amber-200 rounded-lg p-2 text-amber-700">
              {note}
            </p>
          ))}
          {undatedTotal > 0 && (
            <p className="text-[11px] text-gray-400" title={undatedDetail}>
              פירוט הרשומות ללא מועד: {undatedDetail}
            </p>
          )}
        </div>
      )}

      {shown.length === 0 && anySourceLoading ? (
        <div role="status" className="bg-white rounded-xl border shadow-sm p-6 text-center text-sm text-gray-500">
          {online
            ? "טוען את זרם העבודה והראיות…"
            : "אין חיבור לרשת — ממתין לחיבור לפני טעינת זרם העבודה והראיות."}
        </div>
      ) : shown.length === 0 ? (
        <div className="bg-white rounded-xl border shadow-sm p-6 text-center text-sm text-gray-500 space-y-1">
          {activeTypes.size === 0 ? (
            <p>לא נבחרו סוגי אירועים להצגה — הפעילו לפחות סוג אחד.</p>
          ) : activeStates.size > 0 && stateCounts.verified + stateCounts.unverified + stateCounts.failed === 0 ? (
            <p>אין אירועי עבודה עם סטטוס אימות בטווח ובסינון הנוכחי.</p>
          ) : (
            <>
              <p>אין אירועים בסינון הנוכחי.</p>
              <p className="text-xs text-gray-400">אפשר להרחיב את טווח הזמן או לנקות את החיפוש.</p>
            </>
          )}
          {someSourceUnknown && (
            <p className="text-xs text-amber-600">ייתכן וחסרים אירועים — חלק מהמקורות לא נטענו או שאינם זמינים עבורך.</p>
          )}
        </div>
      ) : (
        groups.map((group) => (
          <section key={group.key} className="bg-white rounded-xl border shadow-sm p-5" aria-label={`אירועים — ${group.label}`}>
            <h2 className="font-semibold text-gray-700 text-sm border-b pb-2">{group.label}</h2>
            <ol className="relative border-r border-gray-200 mr-3 mt-3 space-y-4">
              {group.events.map((event) => (
                <EventRow key={event.id} event={event} employeesById={employeesById} />
              ))}
            </ol>
          </section>
        ))
      )}
    </div>
  );
}
